import { useEffect, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { fetchOrgMembers } from '../TimeTracking/Reports/reportsShared';

const INATIVOS = ['archived', 'completed', 'cancelled'];
const VAZIO: string[] = [];

interface Estado { org: string | null; projetos: string[]; pessoas: string[]; loading: boolean; error: boolean }
const INICIAL: Estado = { org: null, projetos: VAZIO, pessoas: VAZIO, loading: false, error: false };

type Status = 'carregando' | 'pronto' | 'erro';

// Carrega projetos ativos e pessoas da organização quando `precisa` fica true.
// Uma vez por organização; depois de um erro, pedir de novo (reabrir o seletor) tenta outra vez.
export function usePickerData(organizationId: string | null | undefined, precisa: boolean) {
  const [estado, setEstado] = useState<Estado>(INICIAL);
  const pedido = useRef<{ org: string; status: Status } | null>(null);
  const reqId = useRef(0);
  const montado = useRef(true);

  useEffect(() => { montado.current = true; return () => { montado.current = false; }; }, []);

  useEffect(() => {
    if (!precisa || !organizationId) return;
    const atual = pedido.current;
    if (atual && atual.org === organizationId && atual.status !== 'erro') return;
    const id = ++reqId.current;
    const reg: { org: string; status: Status } = { org: organizationId, status: 'carregando' };
    pedido.current = reg;
    const valido = () => montado.current && reqId.current === id;
    setEstado({ org: organizationId, projetos: VAZIO, pessoas: VAZIO, loading: true, error: false });
    (async () => {
      try {
        const [proj, membros] = await Promise.all([
          supabase.from('projects').select('id, name, status').eq('organization_id', organizationId),
          fetchOrgMembers(organizationId),
        ]);
        if (proj.error) throw proj.error;
        const cmp = (a: string, b: string) => a.localeCompare(b, 'pt-BR');
        const projetos = ((proj.data ?? []) as { name: string | null; status: string | null }[])
          .filter((p) => p.name && !INATIVOS.includes(p.status ?? ''))
          .map((p) => p.name as string).sort(cmp);
        const pessoas = membros.map((m) => m.name).filter(Boolean).sort(cmp);
        if (reqId.current === id) reg.status = 'pronto';
        if (valido()) setEstado({ org: organizationId, projetos, pessoas, loading: false, error: false });
      } catch {
        if (reqId.current === id) reg.status = 'erro';
        if (valido()) setEstado({ org: organizationId, projetos: VAZIO, pessoas: VAZIO, loading: false, error: true });
      }
    })();
  }, [precisa, organizationId]);

  if (estado.org !== (organizationId ?? null)) return { projetos: VAZIO, pessoas: VAZIO, loading: false, error: false };
  return { projetos: estado.projetos, pessoas: estado.pessoas, loading: estado.loading, error: estado.error };
}
